import { useQuery } from '@tanstack/react-query';
import type { PublicCountryConfig } from '@tenders/shared';
import { useShellSearchParams } from '../lib/use-shell-search-params';
import { useLocale } from '../i18n/locale-context';
import {
  fetchGovSourceCounts,
  parseKindParam,
  parseSourceParam,
  parseStatusParam,
  parseIntParam,
  parseIsoDateParam,
} from '../lib/catalog-client';
import { TabStrip, type TabStripItem } from './tab-strip';
import { countryConfig } from '../country';

/**
 * Every platform a country may list in `govSources`, with its chip label.
 * Exported so a test can pin it to the real configs: a key added to a
 * country's `govSources` without a label here would draw no chip at all, and
 * the API would filter on a platform nobody can pick.
 */
export const LABEL_KEYS: Record<string, string> = {
  goszakup: 'catalog.source.goszakup',
  samruk: 'catalog.source.samruk',
  mitwork: 'catalog.source.mitwork',
  skpharmacy: 'catalog.source.skpharmacy',
  medecc: 'catalog.source.medecc',
  erg: 'catalog.source.erg',
  qarmet: 'catalog.source.qarmet',
  tizilim: 'catalog.source.tizilim',
  ccx: 'catalog.source.ccx',
  eurasiantech: 'catalog.source.eurasiantech',
  'ets-tender': 'catalog.source.etsTender',
  // Kyrgyzstan (manastender.com, 2026-11-04).
  'kg-goszakupki': 'catalog.source.kgGoszakupki',
  'kg-zakupki-gov': 'catalog.source.kgZakupkiGov',
  'kg-zakupki-okmot': 'catalog.source.kgZakupkiOkmot',
};

export interface GovSourceTab {
  value: string;
  labelKey: string;
}

/**
 * The chips THIS deployment draws: «Все» plus every platform in the country's
 * `govSources` that has a label, in the config's order. Uzbekistan lists none,
 * so its catalogue draws no strip; one platform alone draws none either — its
 * chip would filter to the same lots as «Все».
 */
export function visibleGovSourceTabs(
  config: Pick<PublicCountryConfig, 'govSources'> = countryConfig,
): GovSourceTab[] {
  const tabs: GovSourceTab[] = [{ value: '', labelKey: 'catalog.source.all' }];
  for (const key of config.govSources) {
    const labelKey = LABEL_KEYS[key];
    if (labelKey) tabs.push({ value: key, labelKey });
  }
  return tabs.length > 2 ? tabs : [];
}

/**
 * Чипы площадок основного каталога: Все · Госзакупки · Самрук-Казына · Mitwork ·
 * СК-Фармация · Медорганизации · ERG · Qarmet · Tizilim · Caspy Tender · EurasianTech ·
 * ЕТС-Тендер (владелец kaztender.com, 2026-10-19: «сортировка тендеров по
 * источнику для ВСЕХ тендеров, а не только международных»). Та же полоса, что
 * `IfiSourceTabs` на /ifi, но параметр — `source`, а список площадок берётся из
 * конфига страны (`govSources`). На UZ список пуст — компонент не рисует
 * ничего и не делает ни одного запроса.
 *
 * Как и у /ifi, при активном текстовом поиске чип продолжает фильтровать
 * (`GET /catalog/search` принимает `source`), а числа не запрашиваются: точного
 * счёта для ранжированной гибридной выдачи не существует.
 */
export function GovSourceTabs({
  config = countryConfig,
}: { config?: Pick<PublicCountryConfig, 'govSources'> } = {}) {
  const tabs = visibleGovSourceTabs(config);
  // Before any hook: on Uzbekistan this is the whole component.
  if (tabs.length === 0) return null;
  return <GovSourceTabStrip tabs={tabs} config={config} />;
}

function GovSourceTabStrip({
  tabs,
  config,
}: {
  tabs: GovSourceTab[];
  config: Pick<PublicCountryConfig, 'govSources'>;
}) {
  const { t } = useLocale();
  const [params, setParams] = useShellSearchParams();
  const active = parseSourceParam(params.get('source'), config) ?? '';

  const region = params.get('region') ?? undefined;
  const category = params.get('category') ?? undefined;
  const ikpu = params.get('ikpu') ?? undefined;
  const niche = params.get('niche') ?? undefined;
  // Заказчик и минус-слова сужают список — значит, и числа чипов (сервер
  // применяет оба в том же `buildWhere`); без них «Все N» считал больше лотов,
  // чем показывает список под чипами.
  const customer = params.get('customer') ?? undefined;
  const exclude = params.get('exclude') ?? undefined;
  const status = parseStatusParam(params.get('status'));
  const q = params.get('q') ?? undefined;
  // Same guards as IfiSourceTabs: an unrecognised value is "no filter", never
  // passed through to 400 the counts request.
  const minBudgetRaw = parseIntParam(params.get('minBudget'));
  const maxBudgetRaw = parseIntParam(params.get('maxBudget'));
  const deadlineFrom = parseIsoDateParam(params.get('deadlineFrom'));
  const deadlineTo = parseIsoDateParam(params.get('deadlineTo'));
  const since = parseIsoDateParam(params.get('since'));
  const kind = parseKindParam(params.get('kind'));
  const minBudget = minBudgetRaw !== undefined ? Number(minBudgetRaw) : undefined;
  const maxBudget = maxBudgetRaw !== undefined ? Number(maxBudgetRaw) : undefined;

  const { data: counts } = useQuery({
    queryKey: [
      'catalog',
      'gov-source-counts',
      {
        region,
        category,
        ikpu,
        niche,
        customer,
        exclude,
        status,
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
        kind,
      },
    ],
    // Без `scope`: страница основного каталога — дефолтный слой сервера
    // (gov + corporate), в котором и лежат все площадки списка.
    queryFn: () =>
      fetchGovSourceCounts({
        region,
        category,
        ikpu,
        niche,
        customer,
        exclude,
        status,
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
        kind,
      }),
    // Точного числа при текстовом поиске нет — не рисуем врущие числа.
    enabled: !q,
  });

  function select(value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set('source', value);
    else next.delete('source');
    next.delete('page'); // смена чипа сбрасывает пагинацию
    setParams(next);
  }

  function countFor(value: string): number | undefined {
    if (!counts) return undefined;
    return value === '' ? counts.all : counts[value];
  }

  const items: TabStripItem<string>[] = tabs.map((tab) => {
    const count = countFor(tab.value);
    const label = t(tab.labelKey);
    return {
      value: tab.value,
      label,
      count,
      // Цифры в TabStrip помечены aria-hidden, поэтому число — в aria-label.
      ariaLabel: count != null ? t('catalog.source.ariaLabel', { label, count }) : label,
    };
  });

  return (
    <TabStrip
      ariaLabel={t('catalog.source.aria')}
      items={items}
      active={active}
      onSelect={select}
    />
  );
}
